import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Container,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  Menu,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Snackbar,
  ToggleButton,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BusinessIcon from '@mui/icons-material/Business';
import CasinoOutlinedIcon from '@mui/icons-material/CasinoOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import HomeIcon from '@mui/icons-material/Home';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import LandscapeIcon from '@mui/icons-material/Landscape';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import StarIcon from '@mui/icons-material/Star';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AddScopeDialog from '../components/shapeup/AddScopeDialog';
import BetCard from '../components/shapeup/BetCard';
import BetDialog from '../components/shapeup/BetDialog';
import CreateScopeDialog from '../components/shapeup/CreateScopeDialog';
import ChooseCycleDialog from '../components/shapeup/ChooseCycleDialog';
import CloseCycleDialog from '../components/shapeup/CloseCycleDialog';
import CycleDialog from '../components/shapeup/CycleDialog';
import CycleScoreboard from '../components/shapeup/CycleScoreboard';
import CooldownBugPanel from '../components/shapeup/CooldownBugPanel';
import CycleWeekIndicator from '../components/shapeup/CycleWeekIndicator';
import HillLegend from '../components/shapeup/HillLegend';
import MoveNoteDialog from '../components/shapeup/MoveNoteDialog';
import MyReviews from '../components/shapeup/MyReviews';
import useScopeActions from '../components/shapeup/useScopeActions';
import JiraSyncStatus from '../components/shapeup/JiraSyncStatus';
import { EntityCommentsProvider } from '../components/shapeup/useEntityComments';
import { candidateFromOrgMember } from '../components/shapeup/memberRoles';
import { useWorkspace } from '../context/WorkspaceContext';
import { useAuth } from '../context/AuthContext';
import { getOrganizationMembers } from '../utils/organizationsApi';
import {
  archiveCycleSlackChannels,
  closeCycle,
  createBet,
  createBetSlackChannel,
  createCycle,
  createScopeIssue,
  deleteBet,
  getCycleHill,
  getCycleRoster,
  getCycles,
  getShapeUpPeople,
  getShapeUpTags,
  rebet,
  reopenCycle,
  setBetFavorite,
  setCycleBetOrder,
  setCycleRoster,
  syncBetScopes,
  updateBet,
  updateCycle,
} from '../utils/shapeUpApi';
import { fixBug, updateBug } from '../utils/bugsApi';
import { isStale } from '../utils/relativeTime';

const CYCLE_STATUS_LABEL = {
  planning: 'Planning',
  active: 'Active',
  cooldown: 'Cool-down',
  complete: 'Complete',
};

const formatRange = (cycle) => {
  if (!cycle?.startDate && !cycle?.endDate) return null;
  const fmt = (value) => new Date(`${value}T00:00:00`).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
  if (cycle.startDate && cycle.endDate) return `${fmt(cycle.startDate)} – ${fmt(cycle.endDate)}`;
  return fmt(cycle.startDate || cycle.endDate);
};

/**
 * A draggable wrapper around a BetCard. The whole card follows the pointer via
 * the transform on this outer Box, but only the drag handle (spread into
 * BetCard) starts a drag, so the ⋮ menu, Sync, links, and the card body stay
 * clickable. Rendered only while reordering is on; a plain BetCard is used
 * otherwise.
 */
const SortableBetCard = ({ bet, ...betProps }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: bet.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    // Lift the dragged card above its neighbours so it is not clipped by them.
    zIndex: isDragging ? 1 : undefined,
    opacity: isDragging ? 0.85 : undefined,
  };
  return (
    <Box ref={setNodeRef} style={style}>
      <BetCard bet={bet} draggable dragHandleProps={{ ...attributes, ...listeners }} {...betProps} />
    </Box>
  );
};

const ShapeUpPage = () => {
  const { organizations, currentOrganization, isScrumEnabled } = useWorkspace();
  const { user } = useAuth();
  const [, setSearchParams] = useSearchParams();
  // react-router recreates setSearchParams on every URL change, so a scope
  // click (which appends ?scope=<id>) would otherwise churn its identity and
  // re-run the cycle-loading effect below, flashing the skeleton and refetching
  // the whole hill. Hold it in a ref so that effect can leave it out of its deps.
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  // A deep link from the Cycles page can name the org and cycle to open. It is
  // captured once on mount and consumed on the first load, then the params are
  // cleared so later org/cycle changes behave exactly as before.
  const deepLink = useRef(null);
  if (deepLink.current === null) {
    const params = new URLSearchParams(window.location.search);
    deepLink.current = {
      org: params.get('org'),
      cycle: params.get('cycle'),
      consumed: false,
    };
  }

  const [organizationId, setOrganizationId] = useState('');
  const [cycles, setCycles] = useState([]);
  const [selectedCycleId, setSelectedCycleId] = useState('');
  const [hill, setHill] = useState(null);
  // Which cool-down bug is mid-action, so only its own row goes busy rather than
  // the whole panel.
  const [cooldownBusyId, setCooldownBusyId] = useState(null);
  const [loadingCycles, setLoadingCycles] = useState(true);
  const [loadingHill, setLoadingHill] = useState(false);
  const [error, setError] = useState(null);
  // Org-level capabilities from the cycles list, so the New cycle button can
  // gate before any hill has been loaded. Absent means full edit, keeping
  // behaviour unchanged if the server omits them.
  const [orgCapabilities, setOrgCapabilities] = useState(null);
  const [cycleDialogOpen, setCycleDialogOpen] = useState(false);
  // Everyone in the organization, as candidates for a cycle roster, and the
  // selected cycle's roster, which is the only pool a bet team may draw from.
  const [orgPeople, setOrgPeople] = useState([]);
  const [roster, setRoster] = useState([]);
  // What each person has shipped, keyed by userId, so the bet team picker can
  // answer "who has touched this area before" while the decision is being made.
  const [tagsByUser, setTagsByUser] = useState(null);
  // Tags already in use, to suggest in the tag box so the vocabulary converges
  // rather than fragmenting into near-duplicates.
  const [tagSuggestions, setTagSuggestions] = useState([]);
  // The cycle or bet being edited, driving the same dialogs in their edit mode.
  const [editingCycle, setEditingCycle] = useState(null);
  const [editingBet, setEditingBet] = useState(null);
  const [betDialogOpen, setBetDialogOpen] = useState(false);
  const [scopeDialogBet, setScopeDialogBet] = useState(null);
  // The bet whose manual create-scope-by-summary dialog is open (Jira-off mode).
  const [createScopeBet, setCreateScopeBet] = useState(null);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [rebetBet, setRebetBet] = useState(null);
  // Bumped whenever the hill is refetched, which is the signal that a scope
  // changed and the scoreboard's counts are stale.
  const [scoreboardKey, setScoreboardKey] = useState(0);
  // Bumped after a sync pulls Jira comments in, to make the threads refetch.
  const [commentRefresh, setCommentRefresh] = useState(0);
  // This person's own view: show only the bets they have starred. Personal, and
  // it disables dragging while on, since reordering a filtered subset is
  // ambiguous. Persisted per-browser so the filter survives a refresh.
  const [favoritesOnly, setFavoritesOnly] = useState(
    () => localStorage.getItem('shapeUpFavoritesOnly') === 'true'
  );

  useEffect(() => {
    localStorage.setItem('shapeUpFavoritesOnly', favoritesOnly ? 'true' : 'false');
  }, [favoritesOnly]);

  // Anchor for the header's ⋮ overflow menu (less-frequent admin actions).
  const [menuAnchor, setMenuAnchor] = useState(null);
  // The meta/metrics overview is hidden by default so the work leads; the
  // toggle is persisted per-browser, mirroring the favorites filter above.
  const [overviewVisible, setOverviewVisible] = useState(
    () => localStorage.getItem('shapeUpOverviewVisible') === 'true'
  );

  useEffect(() => {
    localStorage.setItem('shapeUpOverviewVisible', overviewVisible ? 'true' : 'false');
  }, [overviewVisible]);

  // A small activation distance means a plain click on the handle (or anywhere)
  // is not read as a drag, so the ⋮ menu, Sync, and links keep working.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  useEffect(() => {
    const dl = deepLink.current;
    if (dl.org && !dl.consumed) {
      setOrganizationId(dl.org);
    } else if (currentOrganization?.id) {
      setOrganizationId(currentOrganization.id);
    } else if (organizations.length > 0) {
      setOrganizationId(organizations[0].id);
    } else {
      setLoadingCycles(false);
    }
  }, [currentOrganization, organizations]);

  const loadCycles = useCallback(async (orgId, preferCycleId) => {
    setLoadingCycles(true);
    setError(null);
    try {
      const data = await getCycles({ organizationId: orgId });
      const list = data.cycles || [];
      setCycles(list);
      setOrgCapabilities(data.capabilities || null);

      const next = preferCycleId
        || list.find(c => c.status === 'active')?.id
        || list[0]?.id
        || '';
      setSelectedCycleId(next);
      if (!next) setHill(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load cycles.');
      setCycles([]);
      setHill(null);
    } finally {
      setLoadingCycles(false);
    }
  }, []);

  useEffect(() => {
    if (!organizationId) return;
    const dl = deepLink.current;
    if (!dl.consumed && (dl.org || dl.cycle)) {
      dl.consumed = true;
      loadCycles(organizationId, dl.cycle || undefined);
      // The link has done its job; drop the params so a later org or cycle
      // change is not re-interpreted as another deep link.
      setSearchParamsRef.current({}, { replace: true });
    } else {
      loadCycles(organizationId);
    }
  }, [organizationId, loadCycles]);

  const loadHill = useCallback(async ({ quiet = false } = {}) => {
    if (!selectedCycleId) return;
    if (!quiet) setLoadingHill(true);
    try {
      const data = await getCycleHill(selectedCycleId);
      setHill(data);
      setScoreboardKey(key => key + 1);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load the hill chart.');
    } finally {
      if (!quiet) setLoadingHill(false);
    }
  }, [selectedCycleId]);

  useEffect(() => {
    if (selectedCycleId) loadHill();
  }, [selectedCycleId, loadHill]);

  // Who could be put on a cycle. One request per organization; a failure leaves
  // the pickers empty rather than breaking the hill.
  useEffect(() => {
    if (!organizationId) {
      setOrgPeople([]);
      return;
    }
    getOrganizationMembers(organizationId)
      .then(members => setOrgPeople((members || []).map(candidateFromOrgMember)))
      .catch(() => setOrgPeople([]));
  }, [organizationId]);

  // Contribution tags. Nice to have rather than load bearing, so a failure just
  // means the picker shows names without the "shipped in" hint.
  useEffect(() => {
    if (!organizationId) {
      setTagsByUser(null);
      return;
    }
    getShapeUpPeople(organizationId)
      .then(data => setTagsByUser((data.people || []).reduce((map, person) => {
        map[person.userId] = person;
        return map;
      }, {})))
      .catch(() => setTagsByUser(null));
  }, [organizationId]);

  const loadTagSuggestions = useCallback(() => {
    if (!organizationId) {
      setTagSuggestions([]);
      return;
    }
    getShapeUpTags(organizationId)
      .then(data => setTagSuggestions(data.tags || []))
      .catch(() => setTagSuggestions([]));
  }, [organizationId]);

  useEffect(() => { loadTagSuggestions(); }, [loadTagSuggestions]);

  const loadRoster = useCallback(async () => {
    if (!selectedCycleId) {
      setRoster([]);
      return;
    }
    try {
      const data = await getCycleRoster(selectedCycleId);
      setRoster(data.members || []);
      // Candidates ride along in the same response, so the bet team picker can
      // offer the whole organization without a second request.
      if (data.candidates) setOrgPeople(data.candidates);
    } catch {
      setRoster([]);
    }
  }, [selectedCycleId]);

  useEffect(() => { loadRoster(); }, [loadRoster]);

  const applyScopeUpdate = useCallback((scopeId, updates) => {
    setHill(prev => prev && ({
      ...prev,
      bets: prev.bets.map(bet => ({
        ...bet,
        scopes: bet.scopes.map(s => (s.id === scopeId ? { ...s, ...updates } : s)),
      })),
    }));
  }, []);

  const {
    toast,
    setToast,
    syncingBetId,
    setSyncingBetId,
    handleMoveScope,
    handleClearOverride,
    handleTogglePark,
    handleExcludeScope,
    handleRenameScope,
    handleHammerScope,
    handleChangeStatus,
    handleChangeAssignee,
    handleRestate,
    handleSync,
    handleSyncCycle,
    syncingCycle,
    handleAddScope,
    noteRequest,
    saveMoveNote,
    dismissNote,
  } = useScopeActions({
    reload: loadHill,
    applyScopeUpdate,
    onSynced: () => setCommentRefresh(n => n + 1),
  });

  // Opening a hill should show the truth, not the last thing someone happened to
  // sync. The background job only reaches organization cycles on a 30-minute
  // cadence, so a personal cycle it never touches, or a tick that just missed,
  // can be well out of date by the time anyone looks. Catch the stale bets up on
  // arrival — once per cycle — and let each card's own health line report a sync
  // that fails rather than interrupting with a toast.
  const caughtUpCycle = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const catchUp = async () => {
      if (!hill || hill.cycle?.id !== selectedCycleId) return;
      if (caughtUpCycle.current === selectedCycleId) return;
      // Claim the cycle before any await, so the quiet reload below cannot start
      // a second pass while this one is still working through the list.
      caughtUpCycle.current = selectedCycleId;

      const stale = (hill.bets || []).filter(bet => bet.jiraEpicKey && isStale(bet.lastSyncedAt));
      if (!stale.length) return;

      for (const bet of stale) {
        if (cancelled) return;
        setSyncingBetId(bet.id);
        try {
          await syncBetScopes(bet.id);
        } catch {
          // Silent on purpose: the card now says when a bet's sync is failing,
          // and the Sync button is there for a deliberate retry.
        } finally {
          setSyncingBetId(null);
        }
      }

      if (!cancelled) {
        await loadHill({ quiet: true });
        setCommentRefresh(n => n + 1);
      }
    };

    catchUp();
    return () => { cancelled = true; };
  }, [hill, selectedCycleId, loadHill, setSyncingBetId]);

  // Claiming and fixing from the cool-down panel. Both reload the hill rather than
  // patching the list in place, because fixing a bug drops it out of the cool-down
  // list entirely and the server decides that, not the client.
  const runCooldownAction = async (bug, action, message) => {
    setCooldownBusyId(bug.id);
    try {
      await action();
      await loadHill({ quiet: true });
      setToast({ severity: 'success', message });
    } catch (err) {
      setToast({ severity: 'error', message: err.response?.data?.message || 'That did not work.' });
    } finally {
      setCooldownBusyId(null);
    }
  };

  const handleClaimCooldownBug = (bug) => runCooldownAction(
    bug,
    () => updateBug(bug.id, organizationId, { assigneeUserId: user?.id }),
    'Yours now.'
  );

  const handleFixCooldownBug = (bug) => runCooldownAction(
    bug,
    () => fixBug(bug.id, organizationId),
    'Fixed, and credited to this cool-down.'
  );

  const handleDeleteBet = async (bet) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete the bet "${bet.name}" and its scopes? Jira is not affected.`)) return;
    try {
      await deleteBet(bet.id);
      await loadHill({ quiet: true });
      setToast({ severity: 'success', message: 'Bet deleted' });
    } catch (err) {
      setToast({ severity: 'error', message: err.response?.data?.message || 'Could not delete the bet.' });
    }
  };

  // A personal star. Flip it in place so it feels instant, then persist; a
  // refusal from the server puts the star back and says so.
  const handleToggleFavorite = async (bet) => {
    const next = !bet.isFavorite;
    setHill(prev => prev && ({
      ...prev,
      bets: prev.bets.map(b => (b.id === bet.id ? { ...b, isFavorite: next } : b)),
    }));
    try {
      await setBetFavorite(bet.id, next);
    } catch (err) {
      setHill(prev => prev && ({
        ...prev,
        bets: prev.bets.map(b => (b.id === bet.id ? { ...b, isFavorite: !next } : b)),
      }));
      setToast({ severity: 'error', message: err.response?.data?.message || 'Could not update the star.' });
    }
  };

  // A personal reorder. Reflect the drop immediately, then save this user's
  // order; on failure, put the old order back and say so.
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const previous = hill?.bets || [];
    const oldIndex = previous.findIndex(b => b.id === active.id);
    const newIndex = previous.findIndex(b => b.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(previous, oldIndex, newIndex);
    setHill(prev => prev && ({ ...prev, bets: reordered }));
    try {
      await setCycleBetOrder(selectedCycleId, reordered.map(b => b.id));
    } catch (err) {
      setHill(prev => prev && ({ ...prev, bets: previous }));
      setToast({ severity: 'error', message: err.response?.data?.message || 'Could not save your order.' });
    }
  };

  const handleCreateCycle = async (payload) => {
    const cycle = await createCycle({ organizationId, ...payload });
    await loadCycles(organizationId, cycle.id);
    setToast({ severity: 'success', message: `Created ${cycle.name}` });
  };

  const handleUpdateCycle = async (payload) => {
    const { members, ...updates } = payload;
    const updated = await updateCycle(editingCycle.id, updates);
    // The roster is a separate resource, so editing a cycle saves it in a second
    // call. Anyone dropped is also taken off their bets, hence the hill refresh.
    if (members) {
      const result = await setCycleRoster(editingCycle.id, members);
      setRoster(result.members || []);
      if (result.removedFromBets > 0) {
        await loadHill({ quiet: true });
      }
    }
    await loadCycles(organizationId, editingCycle.id);
    setToast({ severity: 'success', message: `Updated ${updated.name}` });
  };

  const handleCreateBet = async (payload) => {
    const bet = await createBet(selectedCycleId, payload);
    await loadHill({ quiet: true });
    // A tag invented on this bet should be suggested on the next one.
    loadTagSuggestions();

    // The bet exists either way; the channel is reported separately so a Slack
    // problem reads as something to retry rather than as a failed bet.
    if (bet.slack && !bet.slack.ok) {
      setToast({
        severity: 'warning',
        message: `${bet.name} was placed, but the Slack channel was not created: ${bet.slack.message}`,
      });
      return;
    }

    // A freshly linked epic has nothing on the hill until we pull it in.
    if (bet.jiraEpicKey) {
      setSyncingBetId(bet.id);
      try {
        const result = await syncBetScopes(bet.id);
        await loadHill({ quiet: true });
        setToast({ severity: 'success', message: result.message });
      } catch (err) {
        setToast({
          severity: 'warning',
          message: `Bet created, but the Jira sync failed: ${err.response?.data?.message || err.message}`,
        });
      } finally {
        setSyncingBetId(null);
      }
    } else {
      setToast({
        severity: bet.slack?.unresolved?.length || bet.slack?.failed?.length ? 'warning' : 'success',
        message: bet.slack ? `Created ${bet.name}. ${bet.slack.message}` : `Created ${bet.name}`,
      });
    }
  };

  const handleCreateBetChannel = async (bet) => {
    try {
      const result = await createBetSlackChannel(bet.id);
      await loadHill({ quiet: true });
      setToast({
        severity: result.unresolved?.length || result.failed?.length ? 'warning' : 'success',
        message: result.message,
      });
    } catch (err) {
      setToast({
        severity: 'error',
        message: err.response?.data?.message || 'Could not create the Slack channel.',
      });
    }
  };

  const handleUpdateBet = async (payload) => {
    const updated = await updateBet(editingBet.id, payload);
    await loadHill({ quiet: true });
    loadTagSuggestions();
    setToast({ severity: 'success', message: `Updated ${updated.name}` });
  };

  // Manual mode's "add scope": creates a keyless scope by summary. The dialog
  // hands over { summary }, and the server ignores issueType when Jira is off.
  const handleCreateScope = async (payload) => {
    const created = await createScopeIssue(createScopeBet.id, payload);
    await loadHill({ quiet: true });
    setToast({ severity: 'success', message: `Added ${created.jiraSummary || payload.summary}` });
  };

  const handleCloseCycle = async (outcomes, { archiveChannels = false } = {}) => {
    const result = await closeCycle(selectedCycleId, outcomes);

    // Archiving is a separate call so a Slack problem cannot undo a cycle that is
    // already closed. It gets its own line in the message rather than its own toast.
    let archiveNote = '';
    if (archiveChannels) {
      try {
        const archived = await archiveCycleSlackChannels(selectedCycleId);
        archiveNote = archived.failed?.length
          ? ` ${archived.message}, ${archived.failed.length} could not be archived.`
          : ` ${archived.message}.`;
      } catch (err) {
        archiveNote = ` The cycle closed, but the Slack channels were not archived: ${err.response?.data?.message || err.message}`;
      }
    }

    await loadCycles(organizationId, selectedCycleId);
    await loadHill({ quiet: true });
    setToast({
      severity: 'success',
      message: (result.brokeCircuit > 0
        ? `${result.message}. ${result.brokeCircuit} bet(s) hit the circuit breaker.`
        : result.message) + archiveNote,
    });
  };

  const handleReopenCycle = async () => {
    try {
      const result = await reopenCycle(selectedCycleId);
      await loadCycles(organizationId, selectedCycleId);
      await loadHill({ quiet: true });
      setToast({ severity: 'success', message: `${result.message}. Recorded outcomes were cleared.` });
    } catch (err) {
      setToast({ severity: 'error', message: err.response?.data?.message || 'Could not reopen the cycle.' });
    }
  };

  const handleRebet = async (targetCycleId) => {
    const created = await rebet(rebetBet.id, targetCycleId);
    const target = cycles.find(c => c.id === targetCycleId);
    await loadCycles(organizationId, selectedCycleId);
    setToast({
      severity: 'success',
      message: `${created.name} is attempt ${created.attempt}, in ${target?.name || 'the chosen cycle'}.`,
    });
  };

  const selectedCycle = cycles.find(c => c.id === selectedCycleId);
  const isClosed = selectedCycle?.status === 'complete';

  // Org-level Jira switch, read from the hill payload so the whole tree gates on
  // one value. Manual scopes pick their status from the same map the hill uses.
  const jiraEnabled = hill?.jiraEnabled !== false;
  const statusOptions = hill?.statusPositions ? Object.keys(hill.statusPositions) : [];

  // Default to full edit when the server omits capabilities, so existing roles
  // and older responses behave exactly as before. A viewer gets canEdit=false
  // (no mutations), and only leads get canDelete for the cascading bet delete.
  const orgCaps = orgCapabilities || { canEdit: true, canDelete: true };
  const cycleCaps = hill?.capabilities || { canEdit: true, canDelete: true };

  // The caller's personal signals, applied to what they see. The starred count
  // labels the filter toggle; the filter narrows the list; and dragging is only
  // meaningful on the full list, so it is off whenever the filter is.
  const allBets = hill?.bets || [];
  const favoriteCount = allBets.filter(bet => bet.isFavorite).length;
  const displayBets = favoritesOnly ? allBets.filter(bet => bet.isFavorite) : allBets;
  const canReorder = !favoritesOnly;

  // Cycle-wide Jira freshness for the header badge. The time shown is the oldest
  // bet's sync (null if any is unsynced), so "synced X ago" covers every scope.
  const jiraBets = allBets.filter(bet => bet.jiraEpicKey);
  const oldestSynced = jiraBets.some(bet => !bet.lastSyncedAt)
    ? null
    : jiraBets.reduce((oldest, bet) => (
      !oldest || new Date(bet.lastSyncedAt) < new Date(oldest) ? bet.lastSyncedAt : oldest
    ), null);
  const anyFailing = jiraBets.some(bet => bet.lastSyncOutcome === 'failed');

  if (!loadingCycles && organizations.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <LandscapeIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography variant="h5" gutterBottom>Shape Up needs an organization</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Cycles and bets belong to an organization so your team can share them.
          </Typography>
          <Button variant="contained" component={RouterLink} to="/organizations">
            Set up an organization
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <EntityCommentsProvider organizationId={organizationId} refreshSignal={commentRefresh}>
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        {isScrumEnabled() && (
          <Link
            component={RouterLink}
            to="/dashboard"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
            color="inherit"
            underline="hover"
          >
            <HomeIcon fontSize="small" />
            Dashboard
          </Link>
        )}
        <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <LandscapeIcon fontSize="small" />
          Shape Up
        </Typography>
      </Breadcrumbs>

      <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexGrow: 1, minWidth: 0 }}>
            <Typography variant="h5">{selectedCycle?.name || 'Shape Up'}</Typography>
            <Tooltip
              title={jiraEnabled
                ? 'Track bets on a hill chart. Each dot is a Jira issue, placed by its status until you drag it yourself.'
                : 'Track bets on a hill chart. Each dot is a scope, placed by its status until you drag it yourself.'}
            >
              <InfoOutlinedIcon fontSize="small" color="disabled" />
            </Tooltip>
            {selectedCycle && (
              <Chip
                size="small"
                label={CYCLE_STATUS_LABEL[selectedCycle.status] || selectedCycle.status}
                color={selectedCycle.status === 'active' ? 'success' : 'default'}
                variant={selectedCycle.status === 'active' ? 'filled' : 'outlined'}
              />
            )}
            {selectedCycle && <CycleWeekIndicator cycle={selectedCycle} />}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            {jiraEnabled && jiraBets.length > 0 && (
              <JiraSyncStatus
                lastSyncedAt={oldestSynced}
                failing={anyFailing}
                syncing={syncingCycle}
                canRefresh={cycleCaps.canEdit}
                onRefresh={() => handleSyncCycle(selectedCycleId)}
              />
            )}

            {organizations.length > 1 && (
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Organization</InputLabel>
                <Select
                  value={organizationId}
                  label="Organization"
                  onChange={(e) => setOrganizationId(e.target.value)}
                >
                  {organizations.map(org => (
                    <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {cycles.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Cycle</InputLabel>
                <Select
                  value={selectedCycleId}
                  label="Cycle"
                  onChange={(e) => setSelectedCycleId(e.target.value)}
                >
                  {cycles.map(cycle => (
                    <MenuItem key={cycle.id} value={cycle.id}>{cycle.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {cycleCaps.canEdit && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setBetDialogOpen(true)}
                disabled={!selectedCycleId}
              >
                New bet
              </Button>
            )}

            <IconButton
              aria-label="More actions"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
            >
              <MenuItem
                component={RouterLink}
                to="/shape-up/pitches"
                onClick={() => setMenuAnchor(null)}
              >
                <CasinoOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
                Betting table
              </MenuItem>
              {(orgCaps.canEdit || (cycleCaps.canEdit && selectedCycleId)) && <Divider />}
              {orgCaps.canEdit && (
                <MenuItem onClick={() => { setCycleDialogOpen(true); setMenuAnchor(null); }}>
                  <AddIcon fontSize="small" sx={{ mr: 1 }} />
                  New cycle
                </MenuItem>
              )}
              {cycleCaps.canEdit && selectedCycle && (
                <MenuItem onClick={() => { setEditingCycle(selectedCycle); setMenuAnchor(null); }}>
                  <EditOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
                  Edit cycle
                </MenuItem>
              )}
              {cycleCaps.canEdit && selectedCycleId && (
                isClosed ? (
                  <MenuItem onClick={() => { handleReopenCycle(); setMenuAnchor(null); }}>
                    <LockOpenIcon fontSize="small" sx={{ mr: 1 }} />
                    Reopen cycle
                  </MenuItem>
                ) : (
                  <MenuItem
                    disabled={!hill?.bets?.length}
                    onClick={() => { setCloseDialogOpen(true); setMenuAnchor(null); }}
                  >
                    <FlagOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
                    Close cycle
                  </MenuItem>
                )
              )}
            </Menu>
          </Box>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <MyReviews />

      {loadingCycles ? (
        <>
          <Skeleton variant="rectangular" height={120} sx={{ mb: 2, borderRadius: 2 }} />
          <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 2 }} />
        </>
      ) : cycles.length === 0 ? (
        <Paper elevation={2} sx={{ p: 5, textAlign: 'center' }}>
          <LandscapeIcon sx={{ fontSize: 44, color: 'text.secondary', mb: 1 }} />
          <Typography variant="h6" gutterBottom>Start your first cycle</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            A cycle is a fixed stretch of time, usually six weeks, that holds the bets you have committed to.
          </Typography>
          {orgCaps.canEdit && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCycleDialogOpen(true)}>
              New cycle
            </Button>
          )}
        </Paper>
      ) : (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <HillLegend />
              <ToggleButton
                value="overview"
                size="small"
                selected={overviewVisible}
                onChange={() => setOverviewVisible(v => !v)}
                aria-label="Show the cycle overview"
                sx={{ textTransform: 'none', gap: 0.5 }}
              >
                <InsightsOutlinedIcon fontSize="small" />
                Overview
              </ToggleButton>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {selectedCycleId && (favoriteCount > 0 || favoritesOnly) && (
                <ToggleButton
                  value="favoritesOnly"
                  size="small"
                  selected={favoritesOnly}
                  onChange={() => setFavoritesOnly(v => !v)}
                  aria-label="Show only your favorite bets"
                  sx={{ textTransform: 'none', gap: 0.5 }}
                >
                  <StarIcon fontSize="small" color={favoritesOnly ? 'warning' : 'inherit'} />
                  Favorites only ({favoriteCount})
                </ToggleButton>
              )}
            </Box>
          </Box>

          {overviewVisible && selectedCycleId && (
            <>
              {selectedCycle && (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
                  {formatRange(selectedCycle) && (
                    <Typography variant="body2" color="text.secondary">{formatRange(selectedCycle)}</Typography>
                  )}
                  {organizations.length > 0 && (
                    <Chip
                      size="small"
                      variant="outlined"
                      icon={<BusinessIcon fontSize="small" />}
                      label={organizations.find(o => o.id === organizationId)?.name || 'Organization'}
                    />
                  )}
                </Box>
              )}
              {/* Keyed off the hill so hammering a scope or ticking a checklist item
                  is reflected without a manual refresh. */}
              <CycleScoreboard cycleId={selectedCycleId} refreshKey={scoreboardKey} />
            </>
          )}

          {/* Only during cool-down, and only above the hill, because during a
              cool-down the bugs are the work and the hill is last cycle's record.
              The server sends this list empty for any other status. */}
          {hill?.cycle?.status === 'cooldown' && (
            <CooldownBugPanel
              bugs={hill.cooldownBugs || []}
              currentUserId={user?.id}
              canEdit={hill?.capabilities?.canEdit}
              busyId={cooldownBusyId}
              onClaim={handleClaimCooldownBug}
              onFix={handleFixCooldownBug}
            />
          )}

          {loadingHill ? (
            <Skeleton variant="rectangular" height={320} sx={{ borderRadius: 2 }} />
          ) : favoritesOnly && displayBets.length === 0 ? (
            <Paper elevation={2} sx={{ p: 5, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>No favorites yet</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Star a bet to keep it here. Only you see your favorites.
              </Typography>
              <Button startIcon={<StarIcon />} onClick={() => setFavoritesOnly(false)}>
                Show all bets
              </Button>
            </Paper>
          ) : hill?.bets?.length ? (
            // Two-up on wide screens so several bets read at a glance; a single
            // column below lg keeps each hill chart wide enough to place dots on.
            // The grid gap owns the spacing, so the cards drop their own margin.
            //
            // A DndContext wraps the grid so a bet can be dragged into this
            // person's own order. It is only active on the full list; the
            // filtered "favorites only" view renders plain cards.
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={displayBets.map(bet => bet.id)} strategy={rectSortingStrategy}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, 1fr)' },
                    gap: 3,
                    alignItems: 'start',
                  }}
                >
                  {displayBets.map(bet => {
                    const cardProps = {
                      bet,
                      sx: { mb: 0 },
                      hillMaxWidth: 520,
                      syncing: syncingBetId === bet.id,
                      onSync: handleSync,
                      onMoveScope: handleMoveScope,
                      onClearOverride: handleClearOverride,
                      onTogglePark: handleTogglePark,
                      onExcludeScope: handleExcludeScope,
                      onRenameScope: handleRenameScope,
                      onHammerScope: handleHammerScope,
                      onRestateScope: handleRestate,
                      onChangeStatus: handleChangeStatus,
                      onChangeAssignee: handleChangeAssignee,
                      onCommentsRead: (id) => applyScopeUpdate(id, { unreadCommentCount: 0 }),
                      onAddScope: setScopeDialogBet,
                      onCreateScope: jiraEnabled ? undefined : setCreateScopeBet,
                      onEditBet: cycleCaps.canEdit ? setEditingBet : undefined,
                      onDeleteBet: cycleCaps.canDelete ? handleDeleteBet : undefined,
                      onRebet: setRebetBet,
                      // Offered only where a channel could actually be made, and
                      // only for bets that have not got one.
                      onCreateSlackChannel: cycleCaps.canEdit && hill?.slack?.canCreateChannels
                        ? handleCreateBetChannel
                        : undefined,
                      canEdit: cycleCaps.canEdit,
                      jiraEnabled,
                      attachmentsEnabled: hill?.attachmentsEnabled === true,
                      statusOptions,
                      definitionOfDone: hill?.definitionOfDone || [],
                      dodStandard: hill?.dodStandard || '',
                      isFavorite: bet.isFavorite,
                      onToggleFavorite: handleToggleFavorite,
                    };
                    return canReorder
                      ? <SortableBetCard key={bet.id} {...cardProps} />
                      : <BetCard key={bet.id} {...cardProps} />;
                  })}
                </Box>
              </SortableContext>
            </DndContext>
          ) : (
            <Paper elevation={2} sx={{ p: 5, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>No bets in this cycle yet</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {jiraEnabled
                  ? 'Place a bet and link it to a Jira epic. Its child issues become the scopes on the hill.'
                  : 'Place a bet, then shape it by adding scopes to its hill.'}
              </Typography>
              {cycleCaps.canEdit && (
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setBetDialogOpen(true)}>
                  New bet
                </Button>
              )}
            </Paper>
          )}
        </>
      )}

      <CycleDialog
        open={cycleDialogOpen || Boolean(editingCycle)}
        cycle={editingCycle}
        onClose={() => { setCycleDialogOpen(false); setEditingCycle(null); }}
        onSubmit={editingCycle ? handleUpdateCycle : handleCreateCycle}
        candidates={orgPeople}
        recentCycles={cycles}
      />

      <BetDialog
        open={betDialogOpen || Boolean(editingBet)}
        bet={editingBet}
        onClose={() => { setBetDialogOpen(false); setEditingBet(null); }}
        onSubmit={editingBet ? handleUpdateBet : handleCreateBet}
        organizationId={organizationId}
        jiraEnabled={jiraEnabled}
        rosterCandidates={roster}
        orgCandidates={orgPeople}
        tagsByUser={tagsByUser}
        tagSuggestions={tagSuggestions}
        slackAvailable={hill?.slack?.canCreateChannels === true}
      />

      <AddScopeDialog
        open={Boolean(scopeDialogBet)}
        bet={scopeDialogBet}
        onClose={() => setScopeDialogBet(null)}
        onSubmit={(jiraKey) => handleAddScope(scopeDialogBet.id, jiraKey)}
      />

      <CreateScopeDialog
        open={Boolean(createScopeBet)}
        bet={createScopeBet}
        manual
        onClose={() => setCreateScopeBet(null)}
        onSubmit={handleCreateScope}
      />

      <CloseCycleDialog
        open={closeDialogOpen}
        cycle={selectedCycle}
        bets={hill?.bets || []}
        onClose={() => setCloseDialogOpen(false)}
        onSubmit={handleCloseCycle}
      />

      <ChooseCycleDialog
        open={Boolean(rebetBet)}
        title="Bet on this again"
        description={rebetBet
          ? `${rebetBet.name} becomes attempt ${(rebetBet.attempt || 1) + 1} in the cycle you pick. `
            + 'The epic and pitch come along; the scopes do not, so the work gets shaped again.'
          : ''}
        cycles={cycles}
        excludeCycleId={rebetBet?.cycleId}
        onClose={() => setRebetBet(null)}
        onSubmit={handleRebet}
      />

      <MoveNoteDialog
        open={Boolean(noteRequest)}
        request={noteRequest}
        onSave={saveMoveNote}
        onSkip={dismissNote}
      />

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} variant="filled">
            {toast.message}
          </Alert>
        ) : null}
      </Snackbar>
    </Container>
    </EntityCommentsProvider>
  );
};

export default ShapeUpPage;
